/**
 * PATH: packages/ui/src/calendar/useFocusTrap.ts
 * FILE_ID: UI-CALENDAR-FOCUS-TRAP-001
 * PURPOSE: Keyboard focus trap for the modal detail bottom-sheet — while the
 *          sheet is open, Tab / Shift+Tab cycle within it instead of escaping
 *          to the grid behind the backdrop. Pure DOM, no deps.
 * WHY: The mobile sheet is `aria-modal="true"`; a modal MUST keep keyboard focus
 *      inside it (WCAG 2.4.3 / 2.1.2). The desktop right-rail is a non-modal
 *      `complementary` peek and deliberately does NOT trap (grid stays
 *      reachable), so this is opt-in via the `active` flag.
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────┐   ┌──────────────────┐
 *   │ ref + active │ → │ Tab keydown handler  │ → │ wrap focus first↔ │
 *   │              │   │ (focusable scan)     │   │ last focusable    │
 *   └──────────────┘   └────────────────────┘   └──────────────────┘
 * RELATED:
 *   - ./components/CalendarDetailSheet.tsx: the sole consumer (modal sheet).
 */

import { useEffect, type RefObject } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Enabled, tabbable descendants of `node`, in DOM order. The selector already
 * drops `disabled` controls and `tabindex="-1"`; we additionally skip
 * `aria-hidden`/`hidden` subtrees. We do NOT filter on layout (offsetParent /
 * getClientRects) because the trap only runs on a visible modal surface and
 * those APIs are unreliable under jsdom — keeping this layout-free makes the
 * selection deterministic and unit-testable.
 */
export function getFocusable(node: HTMLElement): HTMLElement[] {
  return Array.from(
    node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      el.getAttribute('aria-hidden') !== 'true' && !el.hasAttribute('hidden'),
  )
}

/**
 * Trap Tab focus within `ref` while `active`. No-op when inactive or the node
 * is absent, so it's safe to call unconditionally from a component that mounts
 * the trapped surface conditionally.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return undefined
    const node = ref.current
    if (!node) return undefined

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !node) return
      const list = getFocusable(node)
      // Nothing focusable inside → keep focus on the container itself.
      if (list.length === 0) {
        e.preventDefault()
        node.focus()
        return
      }
      const first = list[0]
      const last = list[list.length - 1]
      const current = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (current === first || current === node) {
          e.preventDefault()
          last.focus()
        }
      } else if (current === last) {
        e.preventDefault()
        first.focus()
      }
    }

    node.addEventListener('keydown', onKeyDown)
    return () => node.removeEventListener('keydown', onKeyDown)
  }, [ref, active])
}
